/**
 * Admin Verification Management Page
 * Shows ALL users (providers + clients) with filters, override controls and edit modal.
 */
import { useLanguage } from "@/context/AppContext";
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  UserCheck, UserX, Clock, CheckCircle, XCircle, Loader2, RefreshCw,
  ChevronLeft, Edit2, Ban, ShieldCheck, Trash2, Users, X, Crown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminRegistrations() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [editingUser, setEditingUser] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [regRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/registrations`, {
          params: { status: statusFilter, role: roleFilter, limit: 100 },
          withCredentials: true
        }),
        axios.get(`${API}/admin/registrations/stats`, { withCredentials: true })
      ]);
      setRegistrations(regRes.data.registrations || []);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, roleFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-6" data-testid="admin-verification-management">
      {/* Header */}
      <div className="flex items-center gap-4 mb-4">
        <Button variant="outline" onClick={() => navigate('/admin')} className="rounded-full">
          <ChevronLeft className="w-4 h-4 mr-1" />Zurück
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{t('adminPageRegistrations')}</h1>
          <p className="text-gray-500">Alle Registrierungen prüfen und verwalten</p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" />Aktualisieren
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={Clock} value={stats.pending} label="Ausstehend" color="yellow" onClick={() => setStatusFilter('pending')} active={statusFilter === 'pending'} />
          <StatCard icon={CheckCircle} value={stats.approved} label="Genehmigt" color="green" onClick={() => setStatusFilter('approved')} active={statusFilter === 'approved'} />
          <StatCard icon={XCircle} value={stats.rejected} label="Abgelehnt" color="red" onClick={() => setStatusFilter('rejected')} active={statusFilter === 'rejected'} />
          <StatCard icon={UserCheck} value={stats.total_providers} label="Dienstleister" color="blue" onClick={() => { setStatusFilter('all'); setRoleFilter('provider'); }} active={roleFilter === 'provider'} />
          <StatCard icon={Users} value={stats.total_clients} label="Kunden" color="gray" onClick={() => { setStatusFilter('all'); setRoleFilter('client'); }} active={roleFilter === 'client'} />
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap gap-3 items-center">
          <span className="text-sm font-medium text-gray-600">Rolle:</span>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-40" data-testid="role-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Rollen</SelectItem>
              <SelectItem value="provider">Dienstleister</SelectItem>
              <SelectItem value="client">Kunden</SelectItem>
              <SelectItem value="admin">Admins</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm font-medium text-gray-600 ml-2">Status:</span>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Status</SelectItem>
              <SelectItem value="pending">Ausstehend</SelectItem>
              <SelectItem value="approved">Genehmigt</SelectItem>
              <SelectItem value="rejected">Abgelehnt</SelectItem>
              <SelectItem value="blocked">Gesperrt</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader>
          <CardTitle>Benutzer ({registrations.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : registrations.length === 0 ? (
            <p className="text-center py-8 text-gray-500">Keine Einträge</p>
          ) : (
            <div className="space-y-3">
              {registrations.map(reg => (
                <RegistrationRow
                  key={reg.user_id}
                  reg={reg}
                  onEdit={() => setEditingUser(reg)}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Modal */}
      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSaved={() => { setEditingUser(null); fetchData(); }}
        />
      )}
    </div>
  );
}

function StatCard({ icon: Icon, value, label, color, onClick, active }) {
  const colors = {
    yellow: 'bg-yellow-100 text-yellow-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    blue: 'bg-blue-100 text-blue-600',
    gray: 'bg-gray-100 text-gray-600'
  };
  return (
    <Card
      className={`cursor-pointer hover:shadow-md transition-all ${active ? 'ring-2 ring-[#0044CC] shadow-md' : ''}`}
      onClick={onClick}
      data-testid={`stat-${label.toLowerCase()}`}
    >
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}><Icon className="w-5 h-5" /></div>
        <div><p className="text-2xl font-bold">{value ?? 0}</p><p className="text-xs text-gray-500">{label}</p></div>
      </CardContent>
    </Card>
  );
}

function RegistrationRow({ reg, onEdit }) {
  const statusBadge = {
    pending: <Badge className="bg-yellow-100 text-yellow-800">Ausstehend</Badge>,
    approved: <Badge className="bg-green-100 text-green-800">Genehmigt</Badge>,
    rejected: <Badge className="bg-red-100 text-red-800">Abgelehnt</Badge>,
    blocked: <Badge className="bg-red-100 text-red-800">Gesperrt</Badge>
  };
  const roleBadge = reg.role === 'provider'
    ? <Badge className="bg-[#0044CC] text-white">Dienstleister</Badge>
    : reg.role === 'admin'
      ? <Badge className="bg-purple-600 text-white"><Crown className="w-3 h-3 mr-1 inline" />Admin</Badge>
      : <Badge className="bg-gray-200 text-gray-700">Kunde</Badge>;
  const subBadge = reg.subscription
    ? <Badge className="bg-emerald-100 text-emerald-700 text-xs">Abo: {reg.subscription}</Badge>
    : null;

  return (
    <div className="border rounded-lg p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`registration-row-${reg.user_id}`}>
      <div className="flex items-center gap-4 flex-1 min-w-[260px]">
        <Avatar>
          <AvatarImage src={reg.profile_picture} />
          <AvatarFallback className="bg-blue-600 text-white">{reg.name?.charAt(0) || 'U'}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold truncate">{reg.name || '—'}</span>
            {roleBadge}
            {statusBadge[reg.is_blocked ? 'blocked' : reg.approval_status]}
            {subBadge}
          </div>
          <p className="text-sm text-gray-500 truncate">{reg.email}</p>
          {reg.company_name && <p className="text-xs text-gray-400">{reg.company_name}</p>}
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={onEdit} data-testid={`edit-${reg.user_id}`}>
          <Edit2 className="w-4 h-4 mr-1" />Bearbeiten
        </Button>
      </div>
    </div>
  );
}

function EditUserModal({ user, onClose, onSaved }) {
  const [busy, setBusy] = useState('');

  const run = async (action) => {
    setBusy(action);
    try {
      const uid = user.user_id;
      if (action === 'approve') {
        await axios.post(`${API}/admin/registrations/${uid}/approve`, {}, { withCredentials: true });
        toast.success('Genehmigt');
      } else if (action === 'reject') {
        const reason = prompt('Ablehnungsgrund:');
        if (!reason) { setBusy(''); return; }
        await axios.post(`${API}/admin/registrations/${uid}/reject`, { reason }, { withCredentials: true });
        toast.success('Abgelehnt');
      } else if (action === 'verify') {
        await axios.post(`${API}/admin/verify-user/${uid}`, {}, { withCredentials: true });
        toast.success('Verifiziert');
      } else if (action === 'block') {
        if (!window.confirm('Diesen Benutzer wirklich sperren?')) { setBusy(''); return; }
        await axios.post(`${API}/admin/users/${uid}/block`, {}, { withCredentials: true });
        toast.success('Gesperrt');
      } else if (action === 'unblock') {
        await axios.post(`${API}/admin/users/${uid}/unblock`, {}, { withCredentials: true });
        toast.success('Entsperrt');
      } else if (action === 'grant_sub') {
        await axios.post(`${API}/admin/users/${uid}/grant-free-subscription`, {}, { withCredentials: true });
        toast.success('Jahresabo aktiviert');
      } else if (action === 'trial') {
        await axios.post(`${API}/admin/users/${uid}/set-trial`, {}, { withCredentials: true });
        toast.success('30-Tage Testphase gesetzt');
      } else if (action === 'make_admin') {
        if (!window.confirm(`${user.name} (${user.email}) zum Admin befördern?`)) { setBusy(''); return; }
        await axios.post(`${API}/admin/make-admin/${uid}`, {}, { withCredentials: true });
        toast.success('Admin-Rechte erteilt');
      } else if (action === 'delete') {
        if (!window.confirm('Benutzer endgültig löschen?')) { setBusy(''); return; }
        await axios.delete(`${API}/admin/users/${uid}`, { withCredentials: true });
        toast.success('Gelöscht');
        onSaved();
        return;
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="edit-user-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b">
          <div className="flex items-center gap-3">
            <Avatar><AvatarFallback className="bg-[#0044CC] text-white">{user.name?.charAt(0) || 'U'}</AvatarFallback></Avatar>
            <div>
              <h2 className="font-bold">{user.name}</h2>
              <p className="text-xs text-gray-500">{user.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-5">
          <Section title="Aktueller Status">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Rolle" value={user.role} />
              <Field label="Approval" value={user.approval_status || '—'} />
              <Field label="Abonnement" value={user.subscription || '—'} />
              <Field label="Verifiziert" value={user.verified ? 'Ja' : 'Nein'} />
              <Field label="Gesperrt" value={user.is_blocked ? 'Ja' : 'Nein'} />
              <Field label="Admin" value={user.is_admin ? 'Ja' : 'Nein'} />
            </div>
          </Section>

          <Section title="Verifizierung">
            <div className="flex flex-wrap gap-2">
              <ActionBtn busy={busy} action="approve" icon={UserCheck} label="Genehmigen" className="bg-green-600 hover:bg-green-700 text-white" onClick={run} />
              <ActionBtn busy={busy} action="reject" icon={UserX} label="Ablehnen" className="bg-red-600 hover:bg-red-700 text-white" onClick={run} />
              <ActionBtn busy={busy} action="verify" icon={ShieldCheck} label="Verifizieren" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={run} />
            </div>
          </Section>

          <Section title="Abonnement">
            <div className="flex flex-wrap gap-2">
              <ActionBtn busy={busy} action="grant_sub" icon={CheckCircle} label="Jahresabo (gratis)" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={run} />
              <ActionBtn busy={busy} action="trial" icon={Clock} label="30-Tage Testphase" className="bg-[#0044CC] hover:bg-[#003399] text-white" onClick={run} />
            </div>
          </Section>

          <Section title="Account">
            <div className="flex flex-wrap gap-2">
              {user.is_blocked
                ? <ActionBtn busy={busy} action="unblock" icon={ShieldCheck} label="Entsperren" className="bg-gray-600 hover:bg-gray-700 text-white" onClick={run} />
                : <ActionBtn busy={busy} action="block" icon={Ban} label="Sperren" className="bg-orange-600 hover:bg-orange-700 text-white" onClick={run} />
              }
              {!user.is_admin && (
                <ActionBtn busy={busy} action="make_admin" icon={Crown} label="Zum Admin machen" className="bg-purple-600 hover:bg-purple-700 text-white" onClick={run} />
              )}
              {!user.is_admin && (
                <ActionBtn busy={busy} action="delete" icon={Trash2} label="Löschen" className="bg-red-700 hover:bg-red-800 text-white" onClick={run} />
              )}
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ busy, action, icon: Icon, label, className, onClick }) {
  return (
    <Button
      size="sm"
      onClick={() => onClick(action)}
      disabled={!!busy}
      className={`gap-2 ${className || ''}`}
      data-testid={`modal-action-${action}`}
    >
      {busy === action ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
      {label}
    </Button>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest border-b pb-2 mb-3">{title}</p>
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-medium text-gray-800">{value || '—'}</p>
    </div>
  );
}
